import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  X,
  Loader2,
  ChevronRight,
  CalendarClock,
  FileText,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Receipt,
  Ruler,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { formatCents, formatDate } from '@/lib/utils';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import { SectionCard } from '@/components/SectionCard';
import { CountertopTrackingBar } from '@/components/CountertopTrackingBar';
import { CountertopIntakeCards } from '@/components/CountertopIntakeCards';
import { ESTIMATE_STATUS_BADGE, PO_STATUS_BADGE } from '@/lib/badges';
import type { CountertopJobDetailResponse, CountertopJobFull } from '@/lib/api-types';

// ─── Money input → cents ─────────────────────────────────────────────────────
function dollarsToCents(str: string): number {
  return Math.round((parseFloat(str) || 0) * 100);
}

// ─── Modal shell ─────────────────────────────────────────────────────────────

interface ModalShellProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

function ModalShell({ title, onClose, children }: ModalShellProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Quote modal (inquiry → quoted) ──────────────────────────────────────────

function QuoteModal({
  jobId,
  onClose,
  onSaved,
}: {
  jobId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amountStr, setAmountStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const approx_quote = dollarsToCents(amountStr);
    if (approx_quote <= 0) {
      setError('Enter an approximate quote greater than $0.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/quote`, {
        method: 'POST',
        body: JSON.stringify({ approx_quote }),
      });
      toast.success('Approx quote recorded');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Record approx quote" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ct-quote">
            Approximate quote ($) <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              id="ct-quote"
              type="number"
              min="0"
              step="0.01"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              className="pl-7 tabular-nums"
              autoFocus
              disabled={loading}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            A ballpark figure to share with the customer. Move the job to a firm estimate next.
          </p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Save quote'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Create-estimate modal (quoted → advance_paid) ───────────────────────────

function CreateEstimateModal({
  jobId,
  approxQuote,
  onClose,
  onSaved,
}: {
  jobId: number;
  approxQuote: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // The advance can never exceed the quote — default to min($300, quote) so a
  // small quote (e.g. $35) pre-fills the quote itself rather than the $300 floor.
  const quoteCents = approxQuote ?? 0;
  const defaultAdvanceCents = quoteCents > 0 ? Math.min(30000, quoteCents) : 30000;
  const [advanceStr, setAdvanceStr] = useState((defaultAdvanceCents / 100).toFixed(2));
  const [measurementDate, setMeasurementDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const advance_amount = dollarsToCents(advanceStr);
    if (advance_amount < 0) {
      setError('Advance cannot be negative.');
      return;
    }
    if (quoteCents > 0 && advance_amount > quoteCents) {
      setError(`The advance can't exceed the quote (${formatCents(quoteCents)}).`);
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/create-estimate`, {
        method: 'POST',
        body: JSON.stringify({
          advance_amount,
          measurement_date: measurementDate.trim() || undefined,
        }),
      });
      toast.success('Estimate created & advance recorded');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Create estimate & take advance" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Creates a draft estimate priced at the approx quote and records the advance/deposit
          against it.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ct-advance">Advance ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                $
              </span>
              <Input
                id="ct-advance"
                type="number"
                min="0"
                max={quoteCents > 0 ? (quoteCents / 100).toFixed(2) : undefined}
                step="0.01"
                value={advanceStr}
                onChange={(e) => setAdvanceStr(e.target.value)}
                className="pl-7 tabular-nums"
                disabled={loading}
              />
            </div>
            {quoteCents > 0 && (
              <p className="text-xs text-muted-foreground">
                Quote: <span className="font-medium tabular-nums">{formatCents(quoteCents)}</span> —
                the advance can&rsquo;t exceed this.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ct-measure-date">Measurement date</Label>
            <Input
              id="ct-measure-date"
              type="date"
              value={measurementDate}
              onChange={(e) => setMeasurementDate(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Working…' : 'Create estimate'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Set-final-price modal (advance_paid → measured) ─────────────────────────

function SetFinalPriceModal({
  jobId,
  depositFloor,
  onClose,
  onSaved,
}: {
  jobId: number;
  depositFloor: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [finalStr, setFinalStr] = useState('');
  const [finalSqft, setFinalSqft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const final_price = dollarsToCents(finalStr);
    if (final_price <= 0) {
      setError('Enter a final price greater than $0.');
      return;
    }
    if (final_price < depositFloor) {
      setError(`Final price can't be below the deposit collected (${formatCents(depositFloor)}).`);
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/set-final-price`, {
        method: 'POST',
        body: JSON.stringify({
          final_price,
          final_sqft: finalSqft.trim() || undefined,
        }),
      });
      toast.success('Final price set — collect the balance, then raise the PO');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Set final price" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Re-prices the estimate to the measured final price. Collect the balance on the estimate
          next, then raise the supplier PO — no PO is created yet.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="ct-final">
            Final price ($) <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              id="ct-final"
              type="number"
              min="0"
              step="0.01"
              value={finalStr}
              onChange={(e) => setFinalStr(e.target.value)}
              className="pl-7 tabular-nums"
              autoFocus
              disabled={loading}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Can&rsquo;t drop below the deposit collected:{' '}
            <span className="font-medium tabular-nums">{formatCents(depositFloor)}</span>.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ct-final-sqft">Final sq.ft.</Label>
          <Input
            id="ct-final-sqft"
            value={finalSqft}
            onChange={(e) => setFinalSqft(e.target.value)}
            placeholder="e.g. 122 sq ft slab confirmed"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            Free text — the confirmed size from the measurement. Optional; editable later.
          </p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Working…' : 'Set final price'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Raise-PO modal (measured + estimate paid → ordered) ─────────────────────

function RaisePoModal({
  jobId,
  supplierName,
  onClose,
  onSaved,
}: {
  jobId: number;
  supplierName: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [costStr, setCostStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const supplier_cost = dollarsToCents(costStr);
    if (supplier_cost <= 0) {
      setError('Enter the supplier cost (greater than $0).');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/raise-po`, {
        method: 'POST',
        body: JSON.stringify({ supplier_cost }),
      });
      toast.success('Supplier PO raised');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Raise supplier PO" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Raises the purchase order
          {supplierName ? (
            <>
              {' '}
              to <span className="font-medium text-foreground">{supplierName}</span>
            </>
          ) : null}{' '}
          at the agreed cost. The customer balance is already collected.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="ct-cost">
            Supplier cost ($) <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              id="ct-cost"
              type="number"
              min="0"
              step="0.01"
              value={costStr}
              onChange={(e) => setCostStr(e.target.value)}
              className="pl-7 tabular-nums"
              autoFocus
              disabled={loading}
            />
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Working…' : 'Raise PO'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Schedule-install modal (measured → installing) ──────────────────────────

function ScheduleInstallModal({
  jobId,
  onClose,
  onSaved,
}: {
  jobId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [installDate, setInstallDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!installDate.trim()) {
      setError('Pick an installation date.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/schedule-install`, {
        method: 'POST',
        body: JSON.stringify({ install_date: installDate.trim() }),
      });
      toast.success('Installation scheduled');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Schedule installation" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ct-install-date">
            Install date <span className="text-destructive">*</span>
          </Label>
          <Input
            id="ct-install-date"
            type="date"
            value={installDate}
            onChange={(e) => setInstallDate(e.target.value)}
            autoFocus
            disabled={loading}
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Schedule'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Approx sq.ft. modal (free text, any stage) ──────────────────────────────

function ApproxSqftModal({
  jobId,
  current,
  onClose,
  onSaved,
}: {
  jobId: number;
  current: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(current ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/approx-sqft`, {
        method: 'POST',
        body: JSON.stringify({ approx_sqft: value.trim() }),
      });
      toast.success('Approx sq.ft. recorded');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Record approx sq.ft." onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ct-sqft">Approximate sq.ft.</Label>
          <Input
            id="ct-sqft"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. 10×12 ≈ 120 sq ft"
            autoFocus
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            Free text — a rough size from the inquiry. You can refine it later; this doesn&rsquo;t
            change the job status.
          </p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Final sq.ft. modal (free text, edits via PATCH) ─────────────────────────

function FinalSqftModal({
  jobId,
  current,
  onClose,
  onSaved,
}: {
  jobId: number;
  current: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(current ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}`, {
        method: 'PATCH',
        body: JSON.stringify({ final_sqft: value.trim() || null }),
      });
      toast.success('Final sq.ft. updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Edit final sq.ft." onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ct-final-sqft-edit">Final sq.ft.</Label>
          <Input
            id="ct-final-sqft-edit"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. 122 sq ft slab confirmed"
            autoFocus
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            Free text — the confirmed size from the measurement. Doesn&rsquo;t change the job
            status.
          </p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Confirm dialog ──────────────────────────────────────────────────────────

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  confirmVariant = 'default',
  onConfirm,
  onCancel,
  loading,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <ModalShell title={title} onClose={onCancel}>
      <div className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">{message}</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" variant={confirmVariant} onClick={onConfirm} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Working…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Inline date reschedule ──────────────────────────────────────────────────

function DateRow({
  label,
  value,
  jobId,
  field,
  onSaved,
  disabled,
}: {
  label: string;
  value: string | null;
  jobId: number;
  field: 'measurement-date' | 'install-date';
  onSaved: () => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [dateStr, setDateStr] = useState(value ? value.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);

  const bodyKey = field === 'measurement-date' ? 'measurement_date' : 'install_date';

  async function save() {
    setSaving(true);
    try {
      await apiFetch(`/api/countertop-jobs/${jobId}/${field}`, {
        method: 'PATCH',
        body: JSON.stringify({ [bodyKey]: dateStr.trim() || null }),
      });
      toast.success(`${label} updated`);
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update date');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1.5">
          <Input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="h-8 w-40 text-xs"
            disabled={saving}
          />
          <Button size="sm" className="h-8 px-2 text-xs" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs"
            onClick={() => {
              setEditing(false);
              setDateStr(value ? value.slice(0, 10) : '');
            }}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm tabular-nums">
            {value ? formatDate(value) : <span className="text-muted-foreground">Not set</span>}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-primary hover:underline"
            >
              {value ? 'Reschedule' : 'Set'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Audit timeline (admin only) ─────────────────────────────────────────────

interface AuditEntry {
  id: number;
  action: string;
  module: string;
  record_id: number | null;
  detail: string | null;
  full_name: string | null;
  username: string | null;
  created_at: string;
}

interface AuditResponse {
  data: { rows: AuditEntry[] };
}

function prettyAction(action: string): string {
  return action
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Timeline({ jobId }: { jobId: number }) {
  // The audit-log endpoint filters by module + `q` (which matches record_id OR detail),
  // so fetch by module and client-filter to this record exactly. Admin-only endpoint.
  const { data, isLoading } = useQuery({
    queryKey: ['countertop-job-audit', jobId],
    queryFn: () =>
      apiFetch<AuditResponse>(`/api/audit-log?module=countertop_jobs&q=${jobId}&limit=200`),
  });

  const entries = (data?.data.rows ?? []).filter((r) => r.record_id === jobId);

  return (
    <SectionCard icon={CalendarClock} title="Timeline" bodyClassName="p-0">
      {isLoading ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          No activity recorded yet.
        </div>
      ) : (
        <ul className="px-4 py-2">
          {entries.map((e) => (
            <li key={e.id} className="flex gap-3 py-2.5 border-b last:border-0">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary/60" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{prettyAction(e.action)}</p>
                {e.detail && <p className="text-xs text-muted-foreground">{e.detail}</p>}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {e.full_name ?? e.username ?? 'Unknown'} · {e.created_at}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

type StageModal =
  | 'quote'
  | 'create-estimate'
  | 'set-final-price'
  | 'raise-po'
  | 'schedule-install'
  | 'approx-sqft'
  | 'final-sqft'
  | null;

export default function CountertopJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [stageModal, setStageModal] = useState<StageModal>(null);
  const [confirm, setConfirm] = useState<'installed' | 'cancel' | 'delete' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['countertop-job', id],
    queryFn: () => apiFetch<CountertopJobDetailResponse>(`/api/countertop-jobs/${id}`),
    enabled: !!id,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['countertop-job', id] });
    queryClient.invalidateQueries({ queryKey: ['countertop-jobs'] });
    queryClient.invalidateQueries({ queryKey: ['countertop-job-audit', jobId] });
  }

  async function runAction(action: 'installed' | 'cancel' | 'delete') {
    setActionLoading(true);
    try {
      if (action === 'delete') {
        await apiFetch(`/api/countertop-jobs/${id}`, { method: 'DELETE' });
        toast.success('Countertop job deleted');
        queryClient.invalidateQueries({ queryKey: ['countertop-jobs'] });
        navigate('/countertop-jobs');
        return;
      }
      await apiFetch(`/api/countertop-jobs/${id}/${action}`, { method: 'POST' });
      toast.success(action === 'installed' ? 'Marked installed' : 'Job cancelled');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  }

  async function reopenQuote() {
    setReopenLoading(true);
    try {
      await apiFetch(`/api/countertop-jobs/${id}/reopen-quote`, { method: 'POST' });
      toast.success('Quote reopened — you can recreate the estimate');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reopen quote');
    } finally {
      setReopenLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (error || !data?.data.countertop_job) {
    return (
      <div className="p-6">
        <Link
          to="/countertop-jobs"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Countertop Jobs
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Countertop job not found.'}
        </div>
      </div>
    );
  }

  const {
    countertop_job: job,
    estimate,
    purchase_order: po,
    margin,
    estimate_missing: estimateMissing,
  } = data.data;
  const editable = job.status !== 'cancelled' && job.status !== 'installed';
  const canCancel = isAdmin && job.status !== 'cancelled' && job.status !== 'installed';

  // Deposit floor for set-final-price = advance actually collected on the job.
  const depositFloor = job.advance_amount ?? 0;

  // Gating for the contextual stage action.
  const needsSinkSpec = job.kitchen_sink === 'none' && !job.sink_dimensions;
  const estimatePaid = estimate?.status === 'paid';

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back nav */}
      <Link
        to="/countertop-jobs"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Countertop Jobs
      </Link>

      {/* Top job-tracking bar */}
      <CountertopTrackingBar
        job={job}
        margin={margin}
        editable={editable}
        isAdmin={isAdmin}
        editHref={`/countertop-jobs/${id}/edit`}
        onDelete={() => setConfirm('delete')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column — categorised intake + appointments */}
        <div className="lg:col-span-3 space-y-6">
          {/* Categorised intake — Appointments slotted between Site & Access and Material & Scope */}
          <CountertopIntakeCards
            job={job}
            onEditSqft={() => setStageModal('approx-sqft')}
            onEditFinalSqft={() => setStageModal('final-sqft')}
            appointmentsSlot={
              <SectionCard icon={CalendarClock} title="Appointments" bodyClassName="px-4">
                <div className="divide-y">
                  <DateRow
                    label="Measurement date"
                    value={job.measurement_date}
                    jobId={jobId}
                    field="measurement-date"
                    onSaved={invalidate}
                    disabled={!editable}
                  />
                  <DateRow
                    label="Install date"
                    value={job.install_date}
                    jobId={jobId}
                    field="install-date"
                    onSaved={invalidate}
                    disabled={!editable}
                  />
                </div>
              </SectionCard>
            }
          />
        </div>

        {/* Right column — next step + linked records */}
        <aside className="lg:col-span-2 space-y-6">
          {/* Next step — contextual stage action */}
          <SectionCard icon={ChevronRight} title="Next step">
            <StagePanel
              status={job.status}
              hasSupplier={job.supplier_id != null}
              supplierName={job.supplier_name}
              needsSinkSpec={needsSinkSpec}
              estimatePaid={estimatePaid}
              estimateBalance={estimate ? Math.max(estimate.total - estimate.amount_paid, 0) : 0}
              estimateId={estimate?.id ?? null}
              advanceAmount={job.advance_amount}
              estimateMissing={estimateMissing}
              isAdmin={isAdmin}
              reopenLoading={reopenLoading}
              onReopenQuote={reopenQuote}
              onApproxSqft={() => setStageModal('approx-sqft')}
              onQuote={() => setStageModal('quote')}
              onCreateEstimate={() => setStageModal('create-estimate')}
              onSetFinalPrice={() => setStageModal('set-final-price')}
              onRaisePo={() => setStageModal('raise-po')}
              onSchedule={() => setStageModal('schedule-install')}
              onInstalled={() => setConfirm('installed')}
            />
            {canCancel && (
              <div className="mt-4 border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={() => setConfirm('cancel')}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Cancel job
                </Button>
              </div>
            )}
          </SectionCard>

          {/* Linked estimate */}
          <SectionCard icon={FileText} title="Estimate" bodyClassName="p-0">
            {estimate ? (
              <div className="p-4 space-y-2.5">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/estimates/${estimate.id}`}
                    className="font-mono text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {estimate.number}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                  <Badge variant={ESTIMATE_STATUS_BADGE[estimate.status].variant}>
                    {ESTIMATE_STATUS_BADGE[estimate.status].label}
                  </Badge>
                </div>
                {estimate.status === 'draft' ? (
                  <>
                    {/* Draft window (job status 'advance_paid'): recalcEstimate skips
                        drafts, so estimate.amount_paid stays 0 even though the advance
                        was already collected (it lives on the job). Show the real
                        advance and drop the record-payment CTA so the deposit isn't
                        collected a second time. */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Approx. quote</span>
                      <span className="tabular-nums">{formatCents(estimate.total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Advance collected</span>
                      <span className="tabular-nums text-green-700">
                        {formatCents(job.advance_amount ?? 0)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground pt-1 border-t">
                      Draft estimate — the figure above is the approx quote, not the final price.
                      The advance is already recorded; set the final price to bill the balance.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total</span>
                      <span className="tabular-nums">{formatCents(estimate.total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Amount paid</span>
                      <span className="tabular-nums text-green-700">
                        {formatCents(estimate.amount_paid)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-medium pt-1 border-t">
                      <span>Balance</span>
                      <span className="tabular-nums">
                        {formatCents(Math.max(estimate.total - estimate.amount_paid, 0))}
                      </span>
                    </div>
                    <Link to={`/estimates/${estimate.id}`} className="block pt-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Receipt className="h-3.5 w-3.5 mr-1.5" />
                        Open estimate to record payment
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            ) : estimateMissing ? (
              <div className="px-4 py-5 text-sm text-amber-700">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>Estimate deleted — reopen the quote to recreate it.</span>
                </div>
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No estimate yet.
              </div>
            )}
          </SectionCard>

          {/* Linked PO */}
          <SectionCard icon={ShoppingCart} title="Supplier PO" bodyClassName="p-0">
            {po ? (
              <div className="p-4 space-y-2.5">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/purchase-orders/${po.id}`}
                    className="font-mono text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {po.number}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                  <Badge variant={PO_STATUS_BADGE[po.status].variant}>
                    {PO_STATUS_BADGE[po.status].label}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total</span>
                  <span className="tabular-nums">{formatCents(po.total)}</span>
                </div>
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No purchase order yet.
              </div>
            )}
          </SectionCard>

          {/* Attachments */}
          <AttachmentsPanel module="countertop_job" recordId={jobId} />

          {/* Timeline — admin-only (audit-log endpoint is admin-gated) */}
          {isAdmin && <Timeline jobId={jobId} />}
        </aside>
      </div>

      {/* ── Stage modals ── */}
      {stageModal === 'quote' && (
        <QuoteModal jobId={jobId} onClose={() => setStageModal(null)} onSaved={invalidate} />
      )}
      {stageModal === 'create-estimate' && (
        <CreateEstimateModal
          jobId={jobId}
          approxQuote={job.approx_quote}
          onClose={() => setStageModal(null)}
          onSaved={invalidate}
        />
      )}
      {stageModal === 'set-final-price' && (
        <SetFinalPriceModal
          jobId={jobId}
          depositFloor={depositFloor}
          onClose={() => setStageModal(null)}
          onSaved={invalidate}
        />
      )}
      {stageModal === 'raise-po' && (
        <RaisePoModal
          jobId={jobId}
          supplierName={job.supplier_name}
          onClose={() => setStageModal(null)}
          onSaved={invalidate}
        />
      )}
      {stageModal === 'schedule-install' && (
        <ScheduleInstallModal
          jobId={jobId}
          onClose={() => setStageModal(null)}
          onSaved={invalidate}
        />
      )}
      {stageModal === 'approx-sqft' && (
        <ApproxSqftModal
          jobId={jobId}
          current={job.approx_sqft}
          onClose={() => setStageModal(null)}
          onSaved={invalidate}
        />
      )}
      {stageModal === 'final-sqft' && (
        <FinalSqftModal
          jobId={jobId}
          current={job.final_sqft}
          onClose={() => setStageModal(null)}
          onSaved={invalidate}
        />
      )}

      {/* ── Confirm dialogs ── */}
      {confirm === 'installed' && (
        <ConfirmDialog
          title="Mark installed"
          message={`Mark ${job.number} as installed? This completes the job.`}
          confirmLabel="Mark installed"
          onConfirm={() => runAction('installed')}
          onCancel={() => setConfirm(null)}
          loading={actionLoading}
        />
      )}
      {confirm === 'cancel' && (
        <ConfirmDialog
          title="Cancel job"
          message={`Cancel ${job.number}? This cannot be undone.`}
          confirmLabel="Cancel job"
          confirmVariant="destructive"
          onConfirm={() => runAction('cancel')}
          onCancel={() => setConfirm(null)}
          loading={actionLoading}
        />
      )}
      {confirm === 'delete' && (
        <ConfirmDialog
          title="Delete job"
          message={`Delete ${job.number}? It will be removed from the list. This is an admin action.`}
          confirmLabel="Delete"
          confirmVariant="destructive"
          onConfirm={() => runAction('delete')}
          onCancel={() => setConfirm(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
}

// ─── Stage panel (one contextual action per status) ──────────────────────────

function StagePanel({
  status,
  hasSupplier,
  supplierName,
  needsSinkSpec,
  estimatePaid,
  estimateBalance,
  estimateId,
  advanceAmount,
  estimateMissing,
  isAdmin,
  reopenLoading,
  onReopenQuote,
  onApproxSqft,
  onQuote,
  onCreateEstimate,
  onSetFinalPrice,
  onRaisePo,
  onSchedule,
  onInstalled,
}: {
  status: CountertopJobFull['status'];
  hasSupplier: boolean;
  supplierName: string | null;
  needsSinkSpec: boolean;
  estimatePaid: boolean;
  estimateBalance: number;
  estimateId: number | null;
  advanceAmount: number | null;
  estimateMissing: boolean;
  isAdmin: boolean;
  reopenLoading: boolean;
  onReopenQuote: () => void;
  onApproxSqft: () => void;
  onQuote: () => void;
  onCreateEstimate: () => void;
  onSetFinalPrice: () => void;
  onRaisePo: () => void;
  onSchedule: () => void;
  onInstalled: () => void;
}) {
  // Orphaned job: the linked estimate was deleted out from under it. Recovery
  // (reopen the quote) takes priority over the normal stage action.
  if (estimateMissing && status !== 'installed' && status !== 'cancelled') {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 space-y-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-900">The linked estimate was deleted.</p>
            <p className="text-xs text-amber-700">
              This job is stuck — its estimate no longer exists. Reopen the quote to clear the link
              and recreate the estimate.
            </p>
          </div>
        </div>
        {isAdmin ? (
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100 hover:text-amber-900"
            onClick={onReopenQuote}
            disabled={reopenLoading}
          >
            {reopenLoading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Reopen quote
          </Button>
        ) : (
          <p className="text-xs text-amber-700">Ask an admin to reopen the quote.</p>
        )}
      </div>
    );
  }

  switch (status) {
    case 'inquiry':
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button size="sm" variant="outline" onClick={onApproxSqft}>
              <Ruler className="h-3.5 w-3.5 mr-1.5" />
              Record approx sq.ft.
            </Button>
            <Button size="sm" onClick={onQuote}>
              <Receipt className="h-3.5 w-3.5 mr-1.5" />
              Record approx quote
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Recording the quote moves this job to <span className="font-medium">Quoted</span>.
            Sq.ft. is optional and can be refined later.
          </p>
        </div>
      );
    case 'quoted': {
      const blocked = !hasSupplier || needsSinkSpec;
      const hint = !hasSupplier
        ? 'Assign an install supplier first.'
        : needsSinkSpec
          ? 'Enter the sink specification first (edit the job).'
          : null;
      return (
        <div className="space-y-1.5">
          <Button size="sm" onClick={onCreateEstimate} disabled={blocked}>
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Create estimate &amp; take advance
          </Button>
          {hint && <p className="text-xs text-amber-700">{hint}</p>}
        </div>
      );
    }
    case 'advance_paid':
      return (
        <div className="space-y-1.5">
          <Button size="sm" onClick={onSetFinalPrice}>
            <Ruler className="h-3.5 w-3.5 mr-1.5" />
            Set final price
          </Button>
          <p className="text-xs text-muted-foreground">
            Re-prices the estimate to the measured price
            {advanceAmount != null && advanceAmount > 0 ? (
              <> (no lower than the {formatCents(advanceAmount)} deposit collected)</>
            ) : null}
            . The supplier PO comes after the balance is paid.
          </p>
        </div>
      );
    case 'measured':
      // Final price is set; the PO can only be raised once the customer has paid
      // in full. Until then this is an informational waiting state, not an action.
      if (!estimatePaid) {
        return (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 space-y-1">
              <p className="text-sm font-medium text-amber-900">Awaiting customer payment</p>
              <p className="text-xs text-amber-700">
                Collect the balance on the estimate to raise the supplier PO. Payments are recorded
                on the estimate.
              </p>
              <p className="pt-1 text-sm text-amber-900">
                Outstanding balance:{' '}
                <span className="font-semibold tabular-nums">{formatCents(estimateBalance)}</span>
              </p>
            </div>
            {estimateId != null && (
              <Link to={`/estimates/${estimateId}`}>
                <Button size="sm" variant="outline">
                  <Receipt className="h-3.5 w-3.5 mr-1.5" />
                  Open estimate to record payment
                </Button>
              </Link>
            )}
          </div>
        );
      }
      return (
        <div className="space-y-1.5">
          <Button size="sm" onClick={onRaisePo} disabled={!hasSupplier}>
            <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
            Raise PO
          </Button>
          {hasSupplier ? (
            <p className="text-xs text-muted-foreground">
              Balance paid — the PO goes to{' '}
              <span className="font-medium text-foreground">{supplierName}</span>.
            </p>
          ) : (
            <p className="text-xs text-amber-700">
              Assign an install supplier before raising the PO (edit the job).
            </p>
          )}
        </div>
      );
    case 'ordered':
      return (
        <div className="space-y-1.5">
          <Button size="sm" onClick={onSchedule}>
            <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
            Schedule installation
          </Button>
          <p className="text-xs text-muted-foreground">
            PO raised — book the install date to move into installation.
          </p>
        </div>
      );
    case 'installing':
      return (
        <Button size="sm" onClick={onInstalled}>
          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
          Mark installed
        </Button>
      );
    case 'installed':
      return (
        <p className="text-sm text-green-700 inline-flex items-center gap-1.5">
          <CheckCircle className="h-4 w-4" />
          Job complete.
        </p>
      );
    case 'cancelled':
      return (
        <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
          <XCircle className="h-4 w-4" />
          This job was cancelled.
        </p>
      );
    default:
      return null;
  }
}
